import { useStaticCallback } from '@sparemin/blockhead';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { createLayoutSegmentByIdSelector } from 'redux/modules/embed/selectors';
import { millisToSec } from 'utils/time';
import { useEditSceneModalPlaybackContext } from '../EditSceneModalPlaybackContext';
import { useEditSceneModalStateContext } from '../state';
import useEditSceneModal from '../useEditSceneModal';
import useFrameAccurateSeek from '../useFrameAccurateSeek';
import useClipReady from './useClipReady';
import useSeekToClipOnMount from './useSeekToClipOnMount';

export default function usePlayClip() {
  const { playerRef } = useEditSceneModalPlaybackContext();
  const {
    state: { isFrameSeeking },
  } = useEditSceneModalStateContext();
  const { videoId, segmentId } = useEditSceneModal();
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const isEnded = useRef(false);

  const layoutSegment = useSelector(
    createLayoutSegmentByIdSelector(videoId, segmentId),
  );

  const playbackStartMillis = layoutSegment?.playOffsetMillis;
  const playbackEndMillis = playbackStartMillis + layoutSegment?.durationMillis;
  const playbackStartSec = millisToSec(playbackStartMillis);
  const playbackEndSec = millisToSec(playbackEndMillis);

  const handlePlaybackChange = useCallback(
    (newIsPlaying: boolean) => {
      const player = playerRef.current;
      if (!player) {
        return;
      }

      setIsPlaying(newIsPlaying);

      if (newIsPlaying) {
        if (isEnded.current) {
          isEnded.current = false;
          player.currentTime = playbackStartSec;
        }
        player.play();
      } else {
        player.pause();
      }
    },
    [playbackStartSec, playerRef],
  );

  const seekToFrameAccurate = useFrameAccurateSeek();

  const handlePlaybarSeek = useCallback(
    (newCurrentTime: number) => {
      const player = playerRef.current;

      if (!player) {
        return;
      }

      const absoluteSec = playbackStartSec + newCurrentTime;

      setCurrentTime(absoluteSec);
      isEnded.current = false;
      player.currentTime = absoluteSec;
    },
    [playbackStartSec, playerRef],
  );

  const handlePlaybarSeekEnd = useCallback(
    async (newCurrentTime: number) => {
      const player = playerRef.current;

      if (!player) {
        return;
      }

      const absoluteSec = playbackStartSec + newCurrentTime;
      const isSeekingToStart = Math.abs(absoluteSec - playbackStartSec) < 0.01;

      const wasPlaying = !player.paused;

      try {
        // when seeking to start, use frame-accurate seek so that we show the correct
        // first frame
        if (isSeekingToStart) {
          player.pause();
          await seekToFrameAccurate(absoluteSec);
        } else {
          player.currentTime = absoluteSec;
        }
      } catch (error) {
        if (player) {
          player.currentTime = absoluteSec;
        }
      } finally {
        if (wasPlaying) {
          player.play();
        }
      }
    },
    [playbackStartSec, playerRef, seekToFrameAccurate],
  );

  const handleTimeupdate = useCallback(() => {
    if (isFrameSeeking) {
      return;
    }
    const player = playerRef.current;

    if (!player) {
      return;
    }

    const newCurrentTime = player.currentTime;
    setCurrentTime(newCurrentTime);

    if (newCurrentTime >= playbackEndSec) {
      isEnded.current = true;
      handlePlaybackChange(false);
      player.currentTime = playbackEndSec;
    } else {
      isEnded.current = false;
    }
  }, [handlePlaybackChange, isFrameSeeking, playbackEndSec, playerRef]);

  useSeekToClipOnMount({ playbackStartSec });
  useClipReady();

  const staticHandlePlaybackChange = useStaticCallback(handlePlaybackChange);

  useEffect(
    () => () => {
      staticHandlePlaybackChange(false);
    },
    [staticHandlePlaybackChange],
  );

  return {
    currentTime: currentTime - playbackStartSec,
    duration: playbackEndSec - playbackStartSec ?? 0,
    isPlaying,
    handlePlaybackChange,
    handlePlaybarSeek,
    handlePlaybarSeekEnd,
    handleTimeupdate,
  };
}
